import React from "react";
import {
  Box,
  SpaceBetween,
  ColumnLayout,
  Button,
  Link,
} from "@amzn/awsui-components-react";
import MarkdownView from "./components/MarkdownView";

/**
 * RAISE landing / "How RAISE works" page. The copy is static; the centrepiece
 * is the "AI fluency flywheel" — a hand-authored inline SVG (ported from the
 * RAISE prototype). Station labels switch the RAISE tabs via the onNavigate
 * callback from RaiseHome (Training Selection -> Core Curriculum, Traffic /
 * Ongoing Development -> Training Calendar, proof: certification -> Get
 * Certified).
 *
 * Colors are inlined (violet / fuchsia / teal / amber) so the diagram doesn't
 * depend on the prototype's external stylesheet.
 */

const INK = "#26294a";
const VIOLET = "#7C3AED";
const FUCHSIA = "#D6409F";
const TEAL = "#17a2ad";
const AMBER = "#F5A623";

const HANDWRITTEN =
  "'Segoe Print','Bradley Hand','Ink Free','Comic Sans MS',cursive";

// Built-in default for the page title, used when nothing is configured yet.
// All other copy is admin-authored as custom sections (no built-in defaults).
const DEFAULT_PAGE_TITLE = "RAISE - Responsible AI Skills & Expertise Curriculum";

// Prefer a non-blank admin value, else the built-in default.
const orDefault = (value, fallback) =>
  value && String(value).trim() ? value : fallback;

// `content` is the "home" section of the generic content map:
// { page_title: { value, type }, ... }. Reads a field's stored value.
const val = (content, key) => content?.[key]?.value;

const RaiseLanding = ({ onNavigate, content = {} }) => {
  // Switch RAISE tabs via the parent callback (updates active tab + URL).
  const goTab = (tab) => (e) => {
    e.preventDefault();
    if (onNavigate) onNavigate(tab);
  };

  const pageTitle = orDefault(val(content, "page_title"), DEFAULT_PAGE_TITLE);

  // Admin-added custom sections (heading + Markdown body), stored as a JSON
  // array under content key 'custom_sections'. These carry all the page copy
  // (subtitle, intro, how-it-works, etc.) and render above the flywheel SVG.
  let customSections = [];
  const customRaw = val(content, "custom_sections");
  if (customRaw) {
    try {
      const parsed = JSON.parse(customRaw);
      if (Array.isArray(parsed)) customSections = parsed;
    } catch (e) {
      // Malformed stored value — render nothing rather than crash.
    }
  }

  return (
    <SpaceBetween size="l">
      <Box>
        <Box variant="h1" fontSize="display-l">
          <span className="raise-grad-text">{pageTitle}</span>
        </Box>
      </Box>

      <Box>
        {/* Admin-added custom sections (heading + Markdown body), above the SVG. */}
        {customSections.map((section, i) => {
          const heading = (section && section.heading) || "";
          const body = (section && section.body) || "";
          if (!heading && !body) return null;
          return (
            <Box key={i} padding={{ bottom: "s" }}>
              {heading ? <Box variant="h2">{heading}</Box> : null}
              {body ? (
                <Box variant="p">
                  <MarkdownView>{body}</MarkdownView>
                </Box>
              ) : null}
            </Box>
          );
        })}

        {/* AI fluency flywheel — inline SVG (ported from the RAISE prototype). */}
        <figure
          className="raise-napkin"
          role="img"
          aria-label="The AI fluency flywheel. Work loop clockwise: Training Selection, grow your skills, Traffic, Ongoing Development. From Ongoing Development a learning limb runs: work better and faster, then more interesting work, feeding back into Training Selection. Grow your skills links to certification proof. Growth sits at the centre."
        >
          <svg
            viewBox="0 0 1215 760"
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: "100%", height: "auto", fontFamily: HANDWRITTEN }}
          >
            <defs>
              <linearGradient id="raiseFwAur" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor={VIOLET} />
                <stop offset=".52" stopColor={FUCHSIA} />
                <stop offset="1" stopColor={AMBER} />
              </linearGradient>
              <filter id="raiseFwGlow" x="-40%" y="-40%" width="180%" height="180%">
                <feGaussianBlur stdDeviation="26" />
              </filter>
              <filter id="raiseFwLift" x="-35%" y="-35%" width="170%" height="170%">
                <feDropShadow dx="0" dy="6" stdDeviation="10" floodColor="#26294a" floodOpacity=".2" />
              </filter>
            </defs>

            <text x="40" y="58" fontSize="24" fill={INK}>
              AI fluency flywheel
            </text>

            {/* Work loop · centre (660,380) r240 · clockwise */}
            <g stroke={INK} strokeWidth="3" fill="none" strokeLinecap="round">
              <path d="M693.4,142.3 A240,240 0 0 1 897.7,346.6" />
              <path d="M897.7,413.4 A240,240 0 0 1 693.4,617.7" />
              <path d="M626.6,617.7 A240,240 0 0 1 422.3,413.4" />
              <path d="M422.3,346.6 A240,240 0 0 1 626.6,142.3" />
            </g>
            <g stroke={INK} strokeWidth="3" fill="none" strokeLinecap="round">
              <g transform="translate(897.7,346.6) rotate(82)">
                <path d="M0,0 Q-9,-3.5 -18,-9 M0,0 Q-9,3.5 -18,9" />
              </g>
              <g transform="translate(693.4,617.7) rotate(172)">
                <path d="M0,0 Q-9,-3.5 -18,-9 M0,0 Q-9,3.5 -18,9" />
              </g>
              <g transform="translate(422.3,413.4) rotate(262)">
                <path d="M0,0 Q-9,-3.5 -18,-9 M0,0 Q-9,3.5 -18,9" />
              </g>
              <g transform="translate(626.6,142.3) rotate(-8)">
                <path d="M0,0 Q-9,-3.5 -18,-9 M0,0 Q-9,3.5 -18,9" />
              </g>
            </g>

            {/* Learning limb */}
            <g stroke={INK} strokeWidth="2.6" fill="none" strokeLinecap="round">
              <path d="M390,426 C416,500 420,560 340,596" />
              <path d="M135,585 C75,520 60,420 95,330" />
              <path d="M276,234 C355,150 460,108 523,102" />
            </g>
            <g stroke={INK} strokeWidth="2.6" fill="none" strokeLinecap="round">
              <g transform="translate(340,596) rotate(156)">
                <path d="M0,0 Q-8,-3 -16,-8 M0,0 Q-8,3 -16,8" />
              </g>
              <g transform="translate(95,330) rotate(-69)">
                <path d="M0,0 Q-8,-3 -16,-8 M0,0 Q-8,3 -16,8" />
              </g>
              <g transform="translate(525,102) rotate(-5)">
                <path d="M0,0 Q-8,-3 -16,-8 M0,0 Q-8,3 -16,8" />
              </g>
            </g>

            {/* Station dots */}
            <g fill={INK}>
              <circle cx="660" cy="140" r="5" />
              <circle cx="900" cy="380" r="5" />
              <circle cx="660" cy="620" r="5" />
              <circle cx="420" cy="380" r="5" />
            </g>

            {/* Work loop stations (links) */}
            <a href="#curriculum" onClick={goTab("curriculum")} aria-label="Training Selection. Open the Core Curriculum.">
              <text x="660" y="114" textAnchor="middle" fontSize="27" fill={INK}>
                Training Selection
              </text>
              <path d="M562 128 Q660 138 758 126" stroke={VIOLET} strokeWidth="7" strokeLinecap="round" fill="none" opacity=".5" />
            </a>
            <a href="#curriculum" onClick={goTab("curriculum")} aria-label="Grow your skills. Open the Core Curriculum.">
              <text x="922" y="388" fontSize="25" fill={INK}>
                grow your skills
              </text>
              <path d="M922 402 Q1034 411 1146 400" stroke={FUCHSIA} strokeWidth="7" strokeLinecap="round" fill="none" opacity=".55" />
            </a>
            <a href="#calendar" onClick={goTab("calendar")} aria-label="Traffic. Open the Training Calendar.">
              <text x="660" y="670" textAnchor="middle" fontSize="26" fill={INK}>
                Traffic
              </text>
              <path d="M528 684 Q660 694 792 682" stroke={TEAL} strokeWidth="7" strokeLinecap="round" fill="none" opacity=".5" />
            </a>
            <a href="#calendar" onClick={goTab("calendar")} aria-label="Ongoing Development. Open the Training Calendar.">
              <text x="398" y="366" textAnchor="end" fontSize="25" fill={INK}>
                Ongoing
              </text>
              <text x="398" y="398" textAnchor="end" fontSize="25" fill={INK}>
                Development
              </text>
              <path d="M296 412 Q347 421 398 410" stroke={AMBER} strokeWidth="7" strokeLinecap="round" fill="none" opacity=".6" />
            </a>

            {/* Learning limb stations (outcome labels — not links) */}
            <text x="250" y="600" textAnchor="middle" fontSize="23" fill={INK}>
              work better
            </text>
            <text x="250" y="626" textAnchor="middle" fontSize="23" fill={INK}>
              and faster
            </text>
            <path d="M188 640 Q250 649 312 638" stroke={TEAL} strokeWidth="6" strokeLinecap="round" fill="none" opacity=".5" />
            <text x="170" y="246" textAnchor="middle" fontSize="22" fill={INK}>
              more interesting
            </text>
            <text x="170" y="276" textAnchor="middle" fontSize="22" fill={INK}>
              work
            </text>
            <path d="M90 290 Q170 298 250 288" stroke={VIOLET} strokeWidth="6" strokeLinecap="round" fill="none" opacity=".45" />

            {/* Certification annotation off "grow your skills" */}
            <path d="M1012,444 C1004,462 998,478 994,492" fill="none" stroke={INK} strokeWidth="1.8" strokeDasharray="5 5" strokeLinecap="round" />
            <g transform="translate(993,496) rotate(104)" stroke={INK} strokeWidth="1.8" fill="none" strokeLinecap="round">
              <path d="M0,0 Q-7,-2.5 -13,-7 M0,0 Q-7,2.5 -13,7" />
            </g>
            <a href="#certified" onClick={goTab("certified")} aria-label="Proof through certification. Open Get Certified.">
              <text x="1008" y="504" fontSize="18" fill={INK}>
                proof:
              </text>
              <text x="1008" y="528" fontSize="18" fill={INK}>
                certification
              </text>
            </a>

            {/* RAISE centre · aurora · gentle orbit */}
            <g>
              <animateMotion dur="15s" repeatCount="indefinite" path="M0,0 a10,10 0 1,1 -0.01,0 z" />
              <circle cx="660" cy="380" r="196" fill="url(#raiseFwAur)" opacity=".1" filter="url(#raiseFwGlow)" />
              <circle cx="660" cy="380" r="190" fill="url(#raiseFwAur)" filter="url(#raiseFwLift)" />
              <text x="660" y="396" textAnchor="middle" fontSize="62" fontWeight="700" letterSpacing="5" fill="#F6F1E7">
                GROWTH
              </text>
            </g>
          </svg>
        </figure>
      </Box>

      {/* "Where to start" — static three-column guide below the flywheel. The
          inline links and buttons switch RAISE tabs via the onNavigate callback. */}
      <Box>
        <Box variant="h2" padding={{ bottom: "s" }}>Where to start.</Box>
        <ColumnLayout columns={3} borders="vertical">
          <div className="raise-start-col">
            <Box variant="p">
              Head to the{" "}
              <Link href="#curriculum" onFollow={goTab("curriculum")}>Core Curriculum</Link>{" "}
              and begin at your track: AI Aware and AI Enabled are open to everyone
              in accounting, and AI Champion runs through the ATEAM programme.
            </Box>
            <div className="raise-start-action">
              <Button variant="primary" iconAlign="right" iconName="arrow-right" onClick={goTab("curriculum")}>
                Explore the Core Curriculum
              </Button>
            </div>
          </div>

          <div className="raise-start-col">
            <Box variant="p">
              Monthly AI Spotlights add ongoing training and development, and you
              can review past and upcoming sessions on the{" "}
              <Link href="#calendar" onFollow={goTab("calendar")}>Training Calendar</Link>.
            </Box>
            <div className="raise-start-action">
              <Button iconAlign="right" iconName="arrow-right" onClick={goTab("calendar")}>
                View Training Calendar
              </Button>
            </div>
          </div>

          <div className="raise-start-col">
            <Box variant="p">
              When you are ready, take the AI Aware exam as your first milestone;
              see{" "}
              <Link href="#certified" onFollow={goTab("certified")}>Get Certified</Link>.
            </Box>
            <div className="raise-start-action">
              <Button iconAlign="right" iconName="arrow-right" onClick={goTab("certified")}>
                Get Certified
              </Button>
            </div>
          </div>
        </ColumnLayout>
      </Box>
    </SpaceBetween>
  );
};

export default RaiseLanding;
